import React from "react";
import { PayPalButton as ReactPayPalButton } from "react-paypal-button-v2";
import { isMobileView } from "../../../lib/utils";
import queryString from "query-string";

export default class PayPalButton extends React.PureComponent {
  state = {
    hasPaypalSdkLoaded: false,
  };

  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
    this.handleLoadPaypalSDK();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleLoadPaypalSDK = () => {
    // reload paypal sdk with client id & currency matching to that specified by API for current seller
    // [ client id & currency must be passed into the paypal sdk directly through the script tag, so if these change after the sdk is loaded (initial mount of paypal button) a reload is required ]
    // [ if there is no current sdk loaded we don't need to reload bec it means an sdk with non-matching params has not been loaded and the initial load should have the correct params ]
    // note: we are checking if client id or currency has changed bec although as of now the client will be using a single paypal account (so one client id) for all currencies, so only the currency will change per seller,
    //    that may potentially change in the future and multiple accounts will used (= multiple client ids) so we are checking for client id changes as well
    const paypalSDKScript = [...document.scripts].find(
      (s) => s.src.indexOf("www.paypal.com/sdk/js") > 0
    );
    if (paypalSDKScript) {
      const { clientId, currencyCode } = this.props;

      const paypalSDKSrcParts = queryString.parseUrl(paypalSDKScript.src);
      const paypalSDKClientId = paypalSDKSrcParts.query["client-id"];
      const paypalSDKCurrencyCode = paypalSDKSrcParts.query["currency"];

      const resetPaypalSDKSrc =
        paypalSDKClientId !== clientId ||
        paypalSDKCurrencyCode !== currencyCode;
      if (resetPaypalSDKSrc) {
        paypalSDKSrcParts.query["client-id"] = clientId;
        paypalSDKSrcParts.query["currency"] = currencyCode;
        const newPaypalSDKSrc = `${
          paypalSDKSrcParts.url
        }?${queryString.stringify(paypalSDKSrcParts.query)}`;

        document.body.removeChild(paypalSDKScript);

        const newPaypalSDKScript = document.createElement("script");
        newPaypalSDKScript.src = newPaypalSDKSrc;
        newPaypalSDKScript.type = "text/javascript";
        newPaypalSDKScript.async = true;
        newPaypalSDKScript.onload = this.onLoadPaypalSDK;
        document.body.appendChild(newPaypalSDKScript);

        return;
      }
    }

    if (this._isMounted) {
      this.setState({ hasPaypalSdkLoaded: true });
    }
  };

  onLoadPaypalSDK = (script) => {
    if (this._isMounted) {
      this.setState({ hasPaypalSdkLoaded: true });
    }
  };

  createOrder = (_, actions) => {
    const {
      currencyCode = "USD",
      itemPrice = 0,
      itemQuantity = 0,
      processingFeeDonorAmount = 0,
      orderTotal = 0,
      purchaseType,
      chabadHouseName,
    } = this.props;

    const lineItems = [
      {
        name: `${chabadHouseName} - ${purchaseType} Ticket`,
        unit_amount: {
          currency_code: currencyCode,
          value: parseFloat(itemPrice),
        },
        quantity: parseFloat(itemQuantity),
      },
    ];
    if (processingFeeDonorAmount > 0) {
      lineItems.push({
        name: `${chabadHouseName} - ${purchaseType} Donated Fee`,
        unit_amount: {
          currency_code: currencyCode,
          value: parseFloat(processingFeeDonorAmount),
        },
        quantity: parseFloat(1),
      });
    }

    return actions.order.create({
      purchase_units: [
        {
          description: `${chabadHouseName} - ${purchaseType} Purchase`,
          amount: {
            currency_code: currencyCode, //currency_code setting here to ensure that a payment with a mismatched currency to sdk currency doesn't go through
            value: parseFloat(orderTotal),
            breakdown: {
              item_total: {
                currency_code: currencyCode,
                value: parseFloat(orderTotal),
              },
            },
          },
          items: lineItems,
        },
      ],
      application_context: {
        shipping_preference: "NO_SHIPPING",
      },
    });
  };

  onClick = (_, actions) => {
    const { loading, checkIsOrderCompleteOnClick } = this.props;

    //reject if currently loading or incomplete info
    if (loading || !checkIsOrderCompleteOnClick()) {
      return actions.reject();
    } else {
      return actions.resolve();
    }
  };

  onError = (err) => {
    this.props.setErrorMessage(
      "Sorry, something went wrong and your payment could not be completed.  Please try again."
    );
  };

  onApprove = (data) => this.props.submitPayment(data.orderID);

  render() {
    const { clientId, currencyCode, loading } = this.props;

    const { hasPaypalSdkLoaded } = this.state;

    return !hasPaypalSdkLoaded ? (
      <div />
    ) : (
      <div className="paypal-buttons">
        <ReactPayPalButton
          catchError={this.onError}
          createOrder={this.createOrder}
          onCancel={() => console.log("order cancelled")}
          // note: known error in paypal js client sdk for Err Window Closed when user cancels out window before paypal info is loaded [see report in another lib https://github.com/paypal/paypal-checkout-components/issues/1290]
          onClick={this.onClick}
          onError={this.onError}
          onApprove={this.onApprove}
          options={{
            clientId: clientId,
            currency: currencyCode,
            // TODO COC-4537: temporarily enabling venmo only for this tokenized route
            ...(window.location.search?.includes(
              "venmo=VGaNe8nqvQJxNmTKhIu335e2VPCzbe",
            )
              ? {
                  // note: list of avail funding sources here (all disabled): https://developer.paypal.com/docs/checkout/reference/customize-sdk/#disable-funding
                  disableFunding:
                    "card,credit,sepa,bancontact,eps,giropay,ideal,mybank,p24,sofort",
                  enableFunding: "venmo",
                }
              : {
                  disableFunding:
                    "card,credit,venmo,sepa,bancontact,eps,giropay,ideal,mybank,p24,sofort",
                }),
          }}
          style={{
            color: "white",
            height: 48,
            layout: isMobileView() ? "vertical" : "horizontal",
            tagline: false,
          }}
        />

        {loading && (
          <span className="accent-text mt-4">Processing payment...</span>
        )}
      </div>
    );
  }
}
